/**
 * MissionRow — Missões "Suas missões de hoje" list row
 *
 * Layout per Figma 6313:26670:
 *   [16×16 check]  [label, Inter 14/Regular]  [24×24 chevron]
 *
 * Check colours: completed = Jade (#0FA971), pending = Bege Smoke (#DAD8CF).
 * Chevron is rendered ONLY on pending rows (completed rows have no
 * trailing affordance per Figma). Pending rows tap-route to the
 * surface that fulfils the mission via `MISSION_ROUTES`.
 *
 * Mission keys mirror the server canonical list in
 * `services/recoveryPlanService.js`. The Bill mission was removed in the
 * April 2026 design pass — Bill has its own surface and is not a daily
 * trackable mission.
 */

import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { Svg, Path } from 'react-native-svg';
import { useRouter } from 'expo-router';
import { useLanguage } from '@/contexts/LanguageContext';
import type { MissionKey } from '@/services/recoveryPlanService';

interface Props {
  missionKey: MissionKey;
  completed: boolean;
  isLast?: boolean;
  /**
   * `historical` rows are passive: no chevron is rendered (the user can't
   * "finish" a mission for a past date). Completed labels render bold to
   * highlight the day's achievements.
   */
  historical?: boolean;
}

const MISSION_ROUTES: Record<MissionKey, string> = {
  participar_encontro: '/encontros-online',
  registrar_diario_sentimentos: '/diario-sentimento',
  progredir_trilha: '/trail',
  apoiar_membro_comunidade: '/community',
  // "Publicar na comunidade" jumps straight to the composer rather than
  // dropping the user on the feed — the action is to publish, not browse.
  publicar_comunidade: '/community-new-post',
};

// Figma checkmark — 16×16, exact path, strokeWidth 2.
// Jade when completed, Bege Smoke when pending.
const CheckGlyph = ({ completed }: { completed: boolean }) => (
  <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <Path
      d="M12.6673 4.7915L6.25065 11.2082L3.33398 8.2915"
      stroke={completed ? '#0FA971' : '#DAD8CF'}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

// Figma chevron — 24×24, square-cap arrow.
const ChevronGlyph = () => (
  <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <Path
      d="M10 8L14 12L10 16"
      stroke="#151515"
      strokeWidth="2"
      strokeLinecap="square"
    />
  </Svg>
);

export default function MissionRow({
  missionKey,
  completed,
  isLast,
  historical,
}: Props) {
  const router = useRouter();
  const { t } = useLanguage();

  const label = t(`missoes.missions.${missionKey}.label`);
  const showChevron = !historical && !completed;
  const isPassive = historical || completed;

  const handlePress = () => {
    if (isPassive) return;
    const route = MISSION_ROUTES[missionKey];

    // PRD §5.4 — intent point: fire BEFORE router.push so we capture
    // intent volume even if navigation aborts (e.g. routing race).
    import('@/utils/clarityEvents')
      .then(({ logMissoesStarted }) => logMissoesStarted({ mission_id: missionKey }))
      .catch(() => {});

    router.push(route as any);
  };

  return (
    <TouchableOpacity
      style={[styles.row, !isLast && styles.rowSpacing]}
      onPress={handlePress}
      disabled={isPassive}
      activeOpacity={isPassive ? 1 : 0.6}
      accessibilityRole={isPassive ? 'text' : 'button'}
      accessibilityLabel={
        completed ? `${label} — ${t('missoes.missions.doneA11y')}` : label
      }
    >
      <View style={styles.checkSlot}>
        <CheckGlyph completed={completed} />
      </View>
      <Text
        style={[
          styles.label,
          historical && completed && styles.labelHistoricalDone,
        ]}
        numberOfLines={2}
      >
        {label}
      </Text>
      <View style={styles.chevronSlot}>{showChevron && <ChevronGlyph />}</View>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    height: 24,
    width: '100%',
  },
  rowSpacing: {
    // 4px gap between consecutive missions per the latest spec.
    marginBottom: 4,
  },
  checkSlot: {
    width: 16,
    height: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  label: {
    flex: 1,
    color: '#151515',
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 19.6,
  },
  labelHistoricalDone: {
    fontFamily: 'Inter_700Bold',
    fontWeight: '700',
  },
  chevronSlot: {
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
